import React, {useState, useEffect} from 'react'
import {useNavigate, useParams} from 'react-router-dom'
import {PageTitle} from '../../../_metronic/layout/core'
import {CreateUsuarioForm} from './components/CreateUsuarioForm'
import {Usuario, CreateUsuarioData, UpdateUsuarioData, Rol, Funcionalidad} from './core/models'
import {fetchWithAuth} from '../../core/utils/fetchWithAuth'
import {KTIcon} from '../../../_metronic/helpers'

const CreateUsuarioPage: React.FC = () => {
  const navigate = useNavigate()
  const {id} = useParams<{id: string}>()

  const [loading, setLoading] = useState<boolean>(false)
  const [loadingUsuario, setLoadingUsuario] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [usuario, setUsuario] = useState<Usuario | undefined>(undefined)
  const [usuarios, setUsuarios] = useState<Usuario[]>([])
  const [availableRoles, setAvailableRoles] = useState<Rol[]>([])
  const [availableFuncionalidades, setAvailableFuncionalidades] = useState<Funcionalidad[]>([])

  const isEditMode = !!id

  const getToken = (): string | null => {
    return localStorage.getItem('X-TOKEN')
  }

  // Cargar datos del usuario si está en modo edición
  useEffect(() => {
    const loadUsuario = async () => {
      if (!isEditMode || !id) return

      try {
        setLoadingUsuario(true)
        setError(null)
        const token = getToken()
        if (!token) {
          throw new Error('No se encontró el token de autenticación')
        }

        const response = await fetchWithAuth(`${process.env.REACT_APP_API_URL}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-TOKEN': token,
          },
          body: JSON.stringify({
            service: 'getUsuario',
            id: parseInt(id),
          }),
        })

        if (!response.ok) {
          throw new Error(`Error en la petición: ${response.status}`)
        }

        const data = await response.json()

        if (data.code === 'OK') {
          const usuarioData = data.data
          // Los roles y funcionalidades vienen como arrays de IDs, los mapearemos después
          // cuando tengamos las listas completas disponibles
          setUsuario({
            id: usuarioData.id,
            email: usuarioData.email,
            nombre: usuarioData.nombre,
            apellido: usuarioData.apellido,
            es_admin: usuarioData.es_admin,
            activo: usuarioData.activo,
            supervisor_id: usuarioData.supervisor_id || null,
            roles: usuarioData.roles || [], // Array de IDs temporal
            funcionalidades: usuarioData.funcionalidades || [], // Array de IDs temporal
          })
        } else if (
          data.code === 'ERROR' &&
          data.returned_message &&
          data.returned_message.includes('JWT expired')
        ) {
          localStorage.removeItem('X-TOKEN')
          navigate('/auth/login')
          return
        } else {
          throw new Error(data.returned_message || 'Error al cargar el usuario')
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Error desconocido')
      } finally {
        setLoadingUsuario(false)
      }
    }

    loadUsuario()
  }, [id, isEditMode, navigate])

  // Cargar roles, funcionalidades y usuarios para los selects
  useEffect(() => {
    const loadData = async () => {
      try {
        const token = getToken()
        if (!token) return

        // Cargar usuarios para el selector de supervisor
        const usuariosResponse = await fetchWithAuth(`${process.env.REACT_APP_API_URL}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-TOKEN': token,
          },
          body: JSON.stringify({
            service: 'listUsuarios',
            page: 1,
            pageSize: 100, // Cargar muchos usuarios para el selector
            filtros: {},
          }),
        })

        if (usuariosResponse.ok) {
          const usuariosData = await usuariosResponse.json()
          if (usuariosData.code === 'OK') {
            let usuariosArray: Usuario[] = []
            if (Array.isArray(usuariosData.data)) {
              usuariosArray = usuariosData.data
            } else if (usuariosData.data?.usuarios && Array.isArray(usuariosData.data.usuarios)) {
              usuariosArray = usuariosData.data.usuarios
            } else if (usuariosData.data?.data && Array.isArray(usuariosData.data.data)) {
              usuariosArray = usuariosData.data.data
            }
            setUsuarios(usuariosArray)
          }
        }

        // Cargar roles disponibles
        const rolesResponse = await fetchWithAuth(`${process.env.REACT_APP_API_URL}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-TOKEN': token,
          },
          body: JSON.stringify({
            service: 'getRoles',
          }),
        })

        if (rolesResponse.ok) {
          const rolesData = await rolesResponse.json()
          if (rolesData.code === 'OK') {
            setAvailableRoles(rolesData.data || [])
          }
        }

        // Cargar funcionalidades disponibles
        const funcionalidadesResponse = await fetchWithAuth(`${process.env.REACT_APP_API_URL}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-TOKEN': token,
          },
          body: JSON.stringify({
            service: 'getFuncionalidades',
          }),
        })

        if (funcionalidadesResponse.ok) {
          const funcionalidadesData = await funcionalidadesResponse.json()
          if (funcionalidadesData.code === 'OK') {
            setAvailableFuncionalidades(funcionalidadesData.data || [])
          }
        }
      } catch (err) {
        console.error('Error al cargar datos:', err)
      }
    }

    loadData()
  }, [])

  // Mapear IDs de roles y funcionalidades a objetos completos cuando las listas estén disponibles.
  // No sobrescribir con [] si aún no cargamos la lista (evita perder IDs por condición de carrera).
  useEffect(() => {
    if (!usuario) return

    const rolesAreIds = usuario.roles && usuario.roles.length > 0 && typeof usuario.roles[0] === 'number'
    const funcionalidadesAreIds =
      usuario.funcionalidades &&
      usuario.funcionalidades.length > 0 &&
      typeof usuario.funcionalidades[0] === 'number'

    if (!rolesAreIds && !funcionalidadesAreIds) return

    setUsuario((prevUsuario) => {
      if (!prevUsuario) return prevUsuario

      let mappedRoles: Rol[] | undefined
      if (prevUsuario.roles && prevUsuario.roles.length > 0) {
        if (typeof prevUsuario.roles[0] === 'number') {
          const roleIds = prevUsuario.roles as unknown as number[]
          const mapped = roleIds
            .map((roleId) => availableRoles.find((r) => r.id === roleId))
            .filter((r): r is Rol => r !== undefined)
          // Solo actualizar si mapeamos todos (o no hay IDs) para no perder datos si availableRoles llega después
          mappedRoles = availableRoles.length > 0 ? mapped : (prevUsuario.roles as unknown as Rol[])
        } else {
          mappedRoles = prevUsuario.roles as Rol[]
        }
      }

      let mappedFuncionalidades: Funcionalidad[] | undefined
      if (prevUsuario.funcionalidades && prevUsuario.funcionalidades.length > 0) {
        if (typeof prevUsuario.funcionalidades[0] === 'number') {
          const funcionalidadIds = prevUsuario.funcionalidades as unknown as number[]
          const mapped = funcionalidadIds
            .map((funcId) => availableFuncionalidades.find((f) => f.id === funcId))
            .filter((f): f is Funcionalidad => f !== undefined)
          // Solo actualizar cuando tenemos lista de funcionalidades; si no, mantener los IDs para el siguiente run
          mappedFuncionalidades =
            availableFuncionalidades.length > 0 ? mapped : (prevUsuario.funcionalidades as unknown as Funcionalidad[])
        } else {
          mappedFuncionalidades = prevUsuario.funcionalidades as Funcionalidad[]
        }
      }

      return {
        ...prevUsuario,
        ...(mappedRoles !== undefined && {roles: mappedRoles}),
        ...(mappedFuncionalidades !== undefined && {funcionalidades: mappedFuncionalidades}),
      }
    })
  }, [usuario, availableRoles, availableFuncionalidades])

  const handleSubmit = async (usuarioData: CreateUsuarioData | UpdateUsuarioData) => {
    try {
      setLoading(true)
      setError(null)

      const token = getToken()
      if (!token) {
        throw new Error('No se encontró el token de autenticación')
      }

      if (isEditMode) {
        // Actualizar usuario
        const updateData = usuarioData as UpdateUsuarioData
        const response = await fetchWithAuth(`${process.env.REACT_APP_API_URL}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-TOKEN': token,
          },
          body: JSON.stringify({
            service: 'updateUsuario',
            id: parseInt(id!),
            nombre: updateData.nombre,
            apellido: updateData.apellido,
            es_admin: updateData.es_admin,
            supervisor_id: updateData.supervisor_id,
          }),
        })

        if (!response.ok) {
          throw new Error(`Error en la petición: ${response.status}`)
        }

        const data = await response.json()

        if (data.code === 'OK') {
          navigate('/usuarios')
        } else if (
          data.code === 'ERROR' &&
          data.returned_message &&
          data.returned_message.includes('JWT expired')
        ) {
          localStorage.removeItem('X-TOKEN')
          navigate('/auth/login')
          return
        } else {
          throw new Error(data.returned_message || 'Error al actualizar el usuario')
        }
      } else {
        // Crear usuario
        const createData = usuarioData as CreateUsuarioData
        const response = await fetchWithAuth(`${process.env.REACT_APP_API_URL}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-TOKEN': token,
          },
          body: JSON.stringify({
            service: 'createUsuario',
            email: createData.email,
            nombre: createData.nombre,
            apellido: createData.apellido,
            password: createData.password,
            es_admin: createData.es_admin,
            activo: createData.activo,
            supervisor_id: createData.supervisor_id,
          }),
        })

        if (!response.ok) {
          throw new Error(`Error en la petición: ${response.status}`)
        }

        const data = await response.json()

        if (data.code === 'OK') {
          navigate('/usuarios')
        } else if (
          data.code === 'ERROR' &&
          data.returned_message &&
          data.returned_message.includes('JWT expired')
        ) {
          localStorage.removeItem('X-TOKEN')
          navigate('/auth/login')
          return
        } else {
          throw new Error(data.returned_message || 'Error al crear el usuario')
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error desconocido')
    } finally {
      setLoading(false)
    }
  }

  // Mostrar loading mientras se carga el usuario en modo edición
  if (isEditMode && loadingUsuario) {
    return (
      <>
        <PageTitle>Editar Usuario</PageTitle>
        <div className='row g-5 g-xl-8'>
          <div className='col-xl-12'>
            <div className='card'>
              <div className='card-body'>
                <div className='d-flex justify-content-center align-items-center py-10'>
                  <div className='spinner-border text-primary' role='status'>
                    <span className='visually-hidden'>Cargando...</span>
                  </div>
                  <span className='ms-3'>Cargando datos del usuario...</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    )
  }

  // Si está en modo edición y no hay datos del usuario, mostrar error
  if (isEditMode && !loadingUsuario && !usuario) {
    return (
      <>
        <PageTitle>Error</PageTitle>
        <div className='row g-5 g-xl-8'>
          <div className='col-xl-12'>
            <div className='card'>
              <div className='card-body'>
                <div className='alert alert-warning d-flex align-items-center p-5'>
                  <KTIcon iconName='information-5' className='fs-2hx text-warning me-4' />
                  <div className='d-flex flex-column'>
                    <h4 className='mb-1 text-dark'>No se encontraron datos del usuario</h4>
                    <span>{error || 'No se pudo cargar la información del usuario.'}</span>
                  </div>
                  <button
                    className='btn btn-light-primary ms-auto'
                    onClick={() => navigate('/usuarios')}
                  >
                    Volver al listado
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      <PageTitle>{isEditMode ? 'Editar Usuario' : 'Crear Nuevo Usuario'}</PageTitle>

      {/* Breadcrumb */}
      <div className='d-flex justify-content-between align-items-center mb-6'>
        <nav aria-label='breadcrumb'>
          <ol className='breadcrumb'>
            <li className='breadcrumb-item'>
              <button
                className='btn btn-link p-0 text-decoration-none'
                onClick={() => navigate('/usuarios')}
              >
                Usuarios
              </button>
            </li>
            <li className='breadcrumb-item active' aria-current='page'>
              {isEditMode ? 'Editar' : 'Crear'}
            </li>
          </ol>
        </nav>
        <button className='btn btn-light-primary' onClick={() => navigate('/usuarios')}>
          <KTIcon iconName='arrow-left' className='fs-3 me-1' />
          Volver
        </button>
      </div>

      <div className='row g-5 g-xl-8'>
        <div className='col-xl-12'>
          <div className='card'>
            <div className='card-body'>
              {error && (
                <div className='alert alert-danger' role='alert'>
                  {error}
                </div>
              )}

              <CreateUsuarioForm
                onSubmit={handleSubmit}
                loading={loading}
                initialData={usuario}
                isEditMode={isEditMode}
                roles={availableRoles}
                funcionalidades={availableFuncionalidades}
                usuarios={usuarios}
                usuarioId={isEditMode ? parseInt(id!) : undefined}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default CreateUsuarioPage
